import { useTranslation } from "react-i18next";

interface PlainDoorsBoxProps {
  /** The position numbers this booking holds at the station. */
  activeDoors: number[];
}

/**
 * The fallback when no cabinet drawing matches — the compartment as text.
 *
 * ── Why it exists ──────────────────────────────────────────────────────────
 *
 * `ContainerBox` picks a drawing from six hardcoded cabinet types (`cp12`,
 * `cp6`, `cp67`, `cp82`, `cp20`, `kolula12`) and rendered `null` for anything
 * else — and for a depot it could not resolve at all. The station-creation
 * wizard deliberately sends no container type, so the backend writes its
 * default `'locker'` (`inventoryController.ts:82`), which matches none of the
 * six. **Every station created through the wizard therefore showed a title, a
 * void and two buttons** (PO, KGSt-Forum 2026-09-11).
 *
 * ── Why text and not "the closest drawing" ─────────────────────────────────
 *
 * A cabinet drawn to a different model's layout would highlight a position
 * that is not where the customer's compartment physically is. A wrong picture
 * is worse than no picture: it sends someone to the wrong door with
 * confidence. The number is the thing they actually need, so the number is what
 * this shows — correct for every station, including ones nobody has drawn yet.
 *
 * This is also the plainest version of the locker plan the PO asked for. A
 * graphical one is a later step, not a different answer.
 */
const PlainDoorsBox = ({ activeDoors }: PlainDoorsBoxProps) => {
  const { t } = useTranslation();
  const doors = activeDoors.filter((door) => Number.isFinite(door));

  return (
    <div className="flex w-full flex-col items-center gap-4 px-5 py-6 text-center">
      {/* The station name used to sit here and was dropped (PO, 2026-09-14):
          the screen's own heading already says which compartment to open, the
          customer is standing in front of the station while they read it, and
          the number is the only thing on this screen they actually need. */}
      {doors.length > 0 ? (
        <div className="flex flex-col items-center gap-2">
          {/* The label sits ABOVE the box, in the slot the station name used to
              occupy (PO, 2026-09-14) — so the failure screen and this one
              present the compartment identically. Body-text size, matching the
              welcome screen's intro line: fonts stay consistent across the
              flow and only the number is oversized. */}
          <p className="text-[17px] font-medium text-foreground">
            {t("use_order.plain_doors.compartment_label")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {doors.map((door) => (
              <div
                key={door}
                className="min-w-24 rounded-lg bg-foreground/5 px-8 py-5"
              >
                <p className="text-6xl font-semibold leading-tight text-foreground">
                  {door}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* No position at all is a different situation from "we cannot draw
           this cabinet", and it gets its own sentence rather than an empty
           box — a failure must never look like an absence. */
        <p className="text-sm text-foreground/70">
          {t("use_order.plain_doors.no_compartment")}
        </p>
      )}
    </div>
  );
};

export default PlainDoorsBox;
